import { useMutation } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { updateView } from './requests/update'
import {
  ViewUpdateParams,
  ViewResponseDocument,
  ViewUpdateQuery,
} from './types'

type QueryParams<TContext> = {
  onMutate?: (params: ViewUpdateParams) => TContext | Promise<TContext>
  onError?: (
    error: unknown,
    params: ViewUpdateParams,
    context: TContext | undefined
  ) => void
  onSuccess?: (data: ViewResponseDocument, params: ViewUpdateParams) => void
  query?: ViewUpdateQuery
}

const buildRequestBody = ({
  access,
  categoryType,
  endDate,
  id,
  showSmartSwimlaneDatasource,
  swimlaneTitleColumnWidth,
  startDate,
  startMonthIndex,
  startWeekdayIndex,
  timescale,
  title,
}: ViewUpdateParams) => ({
  data: {
    id: id,
    type: 'view' as const,
    attributes: {
      ...(title && { title }),
      ...(timescale && { default_timescale: timescale }),
      start_at: startDate ? formatISO(startDate) : undefined,
      end_at: endDate ? formatISO(endDate) : undefined,
      start_month_index:
        startMonthIndex !== undefined ? startMonthIndex : undefined,
      start_weekday_index:
        startWeekdayIndex !== undefined ? startWeekdayIndex : undefined,
      show_smart_swimlane_datasource:
        showSmartSwimlaneDatasource !== undefined
          ? showSmartSwimlaneDatasource
          : undefined,
      swimlane_title_column_width:
        swimlaneTitleColumnWidth !== undefined
          ? swimlaneTitleColumnWidth
          : undefined,
    },
    ...(access !== undefined && { meta: { access } }),
    relationships: {
      category_type:
        categoryType !== undefined
          ? {
              data: categoryType
                ? { id: categoryType.id, type: 'category_type' as const }
                : null,
            }
          : undefined,
    },
  },
})

export const useUpdatePlan = <TContext = unknown>({
  onMutate,
  onError,
  onSuccess,
  query,
}: QueryParams<TContext>) => {
  return useMutation({
    mutationFn: (params: ViewUpdateParams) =>
      updateView({
        params: {
          path: { view_id: params.id },
          query: { ...query },
        },
        requestBody: buildRequestBody(params),
      }),
    onMutate,
    onError,
    onSuccess,
  })
}
